import { lazy, Suspense } from 'react';
import { projectMarkerGroups } from '../explore/socialMapModel';
import { isGoogleMapsRuntimeAvailable } from '../journey-planning/mapRendererConfig';
import { markerKindLabel } from './journeySpatialLogisticsModel';

const GoogleJourneyActivityMap = lazy(() => import('./GoogleJourneyActivityMap'));

/**
 * AUTH59 — 中性 activity map surface（load／markers／fit／empty／failure）。
 * Planning／Overview 以 title／copy／classPrefix／legend 包裝產品語意。
 * Wave 3 PD-MAP-01：placePickEnabled 時即使尚無 markers 也掛載 Google 地圖供 POI 點選。
 */
export default function JourneyActivityMapSurface({
  markers,
  selectedKey = '',
  onSelectMarker = () => {},
  placePickEnabled = false,
  onPlacePick = null,
  title,
  emptyDescription,
  disabledWarning = '地圖互動尚未啟用；標記仍會以相對位置顯示。',
  showLegend = true,
  classPrefix = 'journey-activity-map',
  testId = 'journey-activity-map',
  emptyTestId = 'journey-activity-map-empty',
  googleTestId = 'journey-activity-map-google',
  loadingTestId = 'journey-activity-map-google-loading',
  planeAriaLabel = 'Journey activity markers',
  mapAriaLabel = 'Journey activity map',
}) {
  const useGoogleRenderer = isGoogleMapsRuntimeAvailable();
  const list = Array.isArray(markers) ? markers : [];
  const projected = projectMarkerGroups(list);
  const showEmptyOnly = list.length === 0 && !(placePickEnabled && useGoogleRenderer);

  if (showEmptyOnly) {
    return (
      <section className={classPrefix} data-testid={emptyTestId}>
        {title ? <h3>{title}</h3> : null}
        <p>{emptyDescription}</p>
        {placePickEnabled && !useGoogleRenderer ? (
          <p className={`${classPrefix}__warning`} role="status">
            地圖選點需啟用 Google 地圖；請改用搜尋或階層選點。
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section className={classPrefix} data-testid={testId}>
      {title ? <h3>{title}</h3> : null}
      {!useGoogleRenderer ? (
        <p className={`${classPrefix}__warning`}>
          {disabledWarning}
        </p>
      ) : null}
      {placePickEnabled ? (
        <p className={`${classPrefix}__pick-hint`} data-testid={`${testId}-pick-hint`}>
          編輯安排時可點地圖上的地點圖示選點；空白處不會建立自訂座標。
        </p>
      ) : null}
      <div className={`${classPrefix}__layout`}>
        {useGoogleRenderer ? (
          <Suspense fallback={<div className={`${classPrefix}__canvas`} data-testid={loadingTestId} />}>
            <GoogleJourneyActivityMap
              markers={list}
              selectedKey={selectedKey}
              onSelectMarker={onSelectMarker}
              placePickEnabled={placePickEnabled}
              onPlacePick={onPlacePick}
              wrapClassName={`${classPrefix}__canvas-wrap`}
              canvasClassName={`${classPrefix}__canvas`}
              warningClassName={`${classPrefix}__warning`}
              testId={googleTestId}
              ariaLabel={mapAriaLabel}
            />
          </Suspense>
        ) : (
          <div className={`${classPrefix}__plane`} role="list" aria-label={planeAriaLabel}>
            {projected.map((marker) => {
              const kind = marker.kind || 'ACTIVITY';
              const kindLabel = marker.kindLabel || markerKindLabel(kind);
              const kindClass = kind === 'STAY'
                ? `${classPrefix}__pin--stay`
                : `${classPrefix}__pin--activity`;
              return (
                <button
                  key={marker.key}
                  type="button"
                  className={`${classPrefix}__pin ${kindClass}${selectedKey === marker.key ? ' is-selected' : ''}`}
                  style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                  data-kind={kind}
                  aria-pressed={selectedKey === marker.key}
                  aria-label={`${kindLabel} · ${marker.title} · ${marker.dayLabel}`}
                  onClick={() => onSelectMarker(marker.key)}
                >
                  <span aria-hidden="true">{kind === 'STAY' ? '宿' : '活'}</span>
                </button>
              );
            })}
          </div>
        )}
        {showLegend && list.length > 0 ? (
          <ul className={`${classPrefix}__legend`} data-testid={`${testId}-legend`}>
            {list.map((marker) => {
              const kind = marker.kind || 'ACTIVITY';
              const kindLabel = marker.kindLabel || markerKindLabel(kind);
              return (
                <li key={`legend-${marker.key}`}>
                  <button
                    type="button"
                    className={selectedKey === marker.key ? 'is-selected' : undefined}
                    data-kind={kind}
                    aria-pressed={selectedKey === marker.key}
                    onClick={() => onSelectMarker(marker.key)}
                  >
                    <span className={`${classPrefix}__legend-kind`}>{kindLabel}</span>
                    {' · '}
                    {marker.title}
                    {' · '}
                    {marker.dayLabel}
                    {' · '}
                    {marker.locationName}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
